import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { eur, dateIt } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import StatusBadge from "@/components/app/StatusBadge";
import PayPalPayment from "@/components/app/PayPalPayment";
import BankTransferPayment from "@/components/app/BankTransferPayment";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, Plus, Trash2, Upload, CreditCard, FileDown, GraduationCap } from "lucide-react";

const empty = { first_name: "", last_name: "", fiscal_code: "", birth_date: "", birth_place: "", email: "", phone: "", company: "", role: "", requires_cfp: false, professional_profile: "", professional_province: "" };
const PROFESSIONAL_PROFILES = ["Ingegnere", "Architetto", "Geometra", "Perito Industriale"];
const STATUSES = ["bozza", "inviato", "in_verifica", "confermato", "completato", "annullato"];

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [order, setOrder] = useState(null);
  const [dialog, setDialog] = useState({ open: false, courseId: null, editionId: null, cfpEnabled: false, cfpProfiles: [] });
  const [form, setForm] = useState(empty);
  const [importing, setImporting] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const partnerLocked = !isAdmin && order?.status && order.status !== "bozza";

  const load = useCallback(async () => {
    const { data } = await api.get(`/orders/${id}`);
    setOrder(data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const addEnrollment = async () => {
    if (!form.first_name?.trim() || !form.last_name?.trim() || !form.email?.trim()
        || !form.fiscal_code?.trim() || !form.birth_date?.trim() || !form.birth_place?.trim()) {
      toast.error("Nome, cognome, email, codice fiscale, data e luogo di nascita sono obbligatori");
      return;
    }
    if (form.requires_cfp) {
      if (!dialog.cfpEnabled) {
        toast.error("Questo corso non prevede il rilascio di CFP.");
        return;
      }
      if (!form.professional_profile?.trim()) {
        toast.error("CFP richiesti: seleziona il Profilo professionale.");
        return;
      }
      if (!(dialog.cfpProfiles || []).includes(form.professional_profile)) {
        toast.error("Profilo professionale non ammesso per questo corso.");
        return;
      }
      if (!form.professional_province?.trim()) {
        toast.error("CFP richiesti: inserisci la Provincia dell'ordine professionale.");
        return;
      }
    }
    try {
      await api.post("/enrollments", { ...form, order_id: id, course_id: dialog.courseId, edition_id: dialog.editionId });
      toast.success("Iscrizione aggiunta");
      setForm(empty); setDialog({ open: false, courseId: null, editionId: null, cfpEnabled: false, cfpProfiles: [] });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore: verifica i dati"); }
  };

  const deleteEnrollment = async (eid) => {
    if (!window.confirm("Confermi l'eliminazione dell'iscritto?")) return;
    await api.delete(`/enrollments/${eid}`);
    toast.success("Iscritto rimosso");
    await load();
  };

  const handleImport = async (e, courseId) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/enrollments/import`, fd, {
        params: { order_id: id, course_id: courseId },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Importati ${data.created} iscritti${data.errors.length ? ` (${data.errors.length} errori)` : ""}`);
      await load();
    } catch (err) { toast.error("Errore importazione file"); }
    finally { setImporting(false); e.target.value = ""; }
  };

  const pay = async (method = "paypal") => {
    await api.post(`/orders/${id}/pay`, { method });
    toast.success("Pagamento registrato (demo)");
    setPayOpen(false); await load();
  };

  const payWithCredit = async () => {
    try {
      await api.post(`/orders/${id}/pay-with-credit`);
      toast.success("Ordine pagato con Credito Partner");
      setPayOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore pagamento credito"); }
  };

  const onPaypalPaid = async () => {
    setPayOpen(false);
    await load();
  };

  const changeStatus = async (v) => {
    await api.put(`/orders/${id}/status`, { status: v });
    toast.success("Stato ordine aggiornato");
    await load();
  };

  const confirmBankTransfer = async () => {
    if (!window.confirm("Confermi di aver ricevuto il bonifico? L'ordine verrà marcato come pagato e verrà generata la pro-forma.")) return;
    try {
      await api.post(`/orders/${id}/bank-transfer/confirm`);
      toast.success("Bonifico confermato · ordine pagato");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore conferma bonifico"); }
  };

  if (!order) return <div className="text-slate-500">Caricamento…</div>;
  const back = isAdmin ? "/admin/ordini" : "/partner/ordini";

  return (
    <div className="space-y-6" data-testid="order-detail">
      <Link to={back} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Torna agli ordini
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">{order.order_number}</h1>
          <div className="text-slate-500 text-sm mt-1">Creato il {dateIt(order.created_at)} · Partner: {order.partner?.company_name}</div>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={order.status} testId="order-status-badge" />
          <StatusBadge status={order.payment_status} testId="order-payment-badge" />
          {isAdmin && (
            <Select value={order.status} onValueChange={changeStatus}>
              <SelectTrigger className="w-40" data-testid="admin-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {isAdmin && order.payment_method === "bonifico" && order.payment_status !== "pagato" && (
            <Button onClick={confirmBankTransfer} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="admin-confirm-bank-transfer">
              Conferma bonifico
            </Button>
          )}
          {!isAdmin && order.payment_status !== "pagato" && (
            <Button onClick={() => setPayOpen(true)} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="pay-order-button">
              <CreditCard className="w-4 h-4 mr-1" /> Paga ora
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200"><CardContent className="p-5"><div className="text-xs uppercase font-bold text-amber-600 tracking-widest">Imponibile</div><div className="font-display text-2xl font-extrabold mt-1">{eur(order.subtotal ?? order.total)}</div></CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-5"><div className="text-xs uppercase font-bold text-amber-600 tracking-widest">IVA</div>{order.vat_exempt ? <div className="text-sm font-semibold mt-2 text-blue-700" data-testid="order-vat-exempt">Non applicata (pagamento con Credito Partner)</div> : <div className="font-display text-2xl font-extrabold mt-1">{eur(order.vat_amount ?? 0)}</div>}</CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-5"><div className="text-xs uppercase font-bold text-amber-600 tracking-widest">Totale</div><div className="font-display text-2xl font-extrabold mt-1 text-[#0F2942]">{eur(order.total)}</div></CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-5"><div className="text-xs uppercase font-bold text-amber-600 tracking-widest">Iscritti</div><div className="font-display text-2xl font-extrabold mt-1">{order.enrollments.length}</div></CardContent></Card>
      </div>
      {!!(order.credit_amount_requested > 0 || (order.payment_breakdown && (order.payment_breakdown.credit || order.payment_breakdown.paypal || order.payment_breakdown.bonifico))) && (
        <Card className="border-slate-200" data-testid="order-payment-breakdown">
          <CardContent className="p-5">
            <h3 className="font-display text-sm font-bold text-slate-700 uppercase tracking-widest mb-3">Dettaglio pagamento</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-xs text-slate-500 uppercase">Totale ordine</div>
                <div className="font-display font-extrabold text-slate-900">{eur(order.total)}</div>
              </div>
              <div>
                <div className="text-xs text-emerald-700 uppercase">Credito Partner</div>
                <div className="font-display font-extrabold text-emerald-700" data-testid="pb-credit">{eur((order.payment_breakdown?.credit ?? order.credit_amount_applied) || 0)}</div>
                {order.payment_status !== "pagato" && order.credit_amount_requested > 0 && (
                  <div className="text-xs text-slate-400">richiesto · scalato dopo pagamento</div>
                )}
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase">PayPal</div>
                <div className="font-display font-extrabold" data-testid="pb-paypal">{eur(order.payment_breakdown?.paypal || 0)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase">Bonifico</div>
                <div className="font-display font-extrabold" data-testid="pb-bank">{eur(order.payment_breakdown?.bonifico || 0)}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {(order.partner_discount_percent ?? 0) > 0 && (
        <Card className="border-emerald-200 bg-emerald-50/40" data-testid="order-discount-summary">
          <CardContent className="p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
              <div>
                <div className="text-xs uppercase font-bold text-emerald-700 tracking-widest">Sconto partner applicato</div>
                <div className="text-slate-600 mt-1">Formula: PrezzoPartner × (1 − {order.partner_discount_percent}% / 100)</div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <div className="text-xs text-slate-500">Imponibile listino</div>
                  <div className="font-semibold line-through text-slate-500">{eur(order.subtotal_before_discount ?? order.subtotal)}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-emerald-700">Sconto -{order.partner_discount_percent}%</div>
                  <div className="font-semibold text-emerald-700">-{eur(order.discount_amount ?? 0)}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500">Imponibile scontato</div>
                  <div className="font-display font-extrabold text-[#0F2942]">{eur(order.subtotal)}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {order.vat_breakdown && order.vat_breakdown.length > 0 && !order.vat_exempt && (
        <Card className="border-slate-200" data-testid="order-vat-breakdown">
          <CardContent className="p-5">
            <h3 className="font-display text-sm font-bold text-slate-700 uppercase tracking-widest mb-3">Dettaglio IVA</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="col-span-2 md:col-span-4 grid grid-cols-4 gap-2 font-semibold text-slate-500 uppercase text-xs tracking-wider border-b border-slate-100 pb-2">
                <div>Aliquota</div><div className="text-right">Imponibile</div><div className="text-right">IVA</div><div className="text-right">Totale</div>
              </div>
              {order.vat_breakdown.map((b) => (
                <div key={b.rate} className="col-span-2 md:col-span-4 grid grid-cols-4 gap-2" data-testid={`order-vat-row-${b.rate}`}>
                  <div><span className={`status-pill ${b.rate === 0 ? "bg-emerald-100 text-emerald-700" : "bg-indigo-100 text-indigo-700"}`}>{b.rate}%</span></div>
                  <div className="text-right font-mono">{eur(b.net)}</div>
                  <div className="text-right font-mono">{eur(b.vat)}</div>
                  <div className="text-right font-mono font-semibold">{eur(b.gross)}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {order.payment_status === "pagato" && (
        <div className="flex justify-end">
          <a href={`${API}/orders/${order.id}/invoice.pdf`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" data-testid="download-invoice">
              <FileDown className="w-4 h-4 mr-1" /> Scarica pro-forma PDF
            </Button>
          </a>
        </div>
      )}

      <Card className="border-slate-200">
        <CardContent className="p-6">
          <h2 className="font-display text-xl font-bold mb-4">Corsi acquistati</h2>
          <div className="space-y-4">
            {order.items.map((it) => {
              const enrolls = order.enrollments.filter((e) => e.course_id === it.course_id);
              const full = enrolls.length >= it.quantity;              return (
                <div key={it.course_id} className="border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="font-semibold text-slate-900">{it.course_title}</div>
                      {it.edition_label && <div className="text-xs font-semibold text-sky-700" data-testid={`order-item-edition-${it.course_id}`}>Edizione: {it.edition_label}</div>}
                      <div className="text-xs text-slate-500">
                        {it.quantity}× · {typeof it.unit_price_final === "number" && typeof it.unit_price_original === "number" && it.unit_price_final < it.unit_price_original ? (
                          <>
                            <span className="line-through">{eur(it.unit_price_original)}</span>{" "}
                            <span className="font-semibold text-slate-900">{eur(it.unit_price_final)}</span>{" "}
                            <span className="text-emerald-700 font-semibold" data-testid={`order-item-discount-${it.course_id}`}>{it.pricing_mode === "custom" ? "(Prezzo dedicato)" : `(-${it.partner_discount_percent ?? order.partner_discount_percent ?? 0}%)`}</span>
                          </>
                        ) : (
                          <>{eur(it.unit_price_final ?? it.unit_price)} cad.</>
                        )}
                        {" · "}
                        <span className={full ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}>{enrolls.length}/{it.quantity} iscritti</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {partnerLocked && (
                        <span className="text-xs text-slate-500 italic px-2 py-1 rounded-md bg-slate-100 border border-slate-200" data-testid="enrollments-locked-notice">
                          Ordine inviato · modifiche iscritti bloccate. Contatta l'assistenza.
                        </span>
                      )}
                      {!partnerLocked && (
                        <>
                          <label className="inline-flex">
                            <input type="file" accept=".csv,.xlsx,.xls" className="hidden"
                              onChange={(e) => handleImport(e, it.course_id)}
                              data-testid={`import-file-${it.course_id}`} disabled={full} />
                            <Button variant="outline" size="sm" asChild disabled={importing || full}>
                              <span><Upload className="w-4 h-4 mr-1" /> Importa CSV/Excel</span>
                            </Button>
                          </label>
                          <Button size="sm" className="bg-[#0F2942] hover:bg-[#1B3B5F]"
                            disabled={full}
                            onClick={() => { setForm(empty); setDialog({ open: true, courseId: it.course_id, editionId: it.edition_id || null, cfpEnabled: !!it.cfp_enabled, cfpProfiles: it.cfp_profiles || [] }); }}
                            data-testid={`add-enrollment-${it.course_id}`}>
                            <Plus className="w-4 h-4 mr-1" /> {full ? "Slot pieni" : "Aggiungi iscritto"}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {enrolls.length > 0 && (
                    <Table className="mt-4">
                      <TableHeader><TableRow>
                        <TableHead>Nome</TableHead><TableHead>Cognome</TableHead><TableHead>CF</TableHead>
                        <TableHead>Email</TableHead><TableHead>Azienda</TableHead><TableHead>Stato</TableHead><TableHead></TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {enrolls.map((e) => (
                          <TableRow key={e.id} data-testid={`enrollment-row-${e.id}`}>
                            <TableCell>{e.first_name}</TableCell>
                            <TableCell>{e.last_name}</TableCell>
                            <TableCell className="font-mono text-xs">{e.fiscal_code}</TableCell>
                            <TableCell>{e.email}</TableCell>
                            <TableCell>{e.company}</TableCell>
                            <TableCell><StatusBadge status={e.status} /></TableCell>
                            <TableCell>{partnerLocked ? (
                              <span className="text-slate-300 text-xs italic">—</span>
                            ) : (
                              <Button variant="ghost" size="icon" onClick={() => deleteEnrollment(e.id)} data-testid={`delete-enrollment-${e.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                            )}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex gap-2">
            <a href={`${API}/export/enrollments`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" data-testid="export-order-enrollments"><FileDown className="w-4 h-4 mr-1" />Esporta iscritti (CSV)</Button>
            </a>
          </div>
          <div className="text-xs text-slate-400 mt-4">
            Import CSV/Excel · colonne supportate: nome/first_name, cognome/last_name, codice_fiscale, email, azienda, mansione, telefono, data_nascita
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialog.open} onOpenChange={(v) => setDialog((d) => ({ ...d, open: v }))}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuovo iscritto</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {[
              ["first_name", "Nome *"], ["last_name", "Cognome *"], ["fiscal_code", "Codice Fiscale *"],
              ["birth_date", "Data nascita *"], ["birth_place", "Luogo di nascita *"], ["email", "Email *"],
              ["phone", "Telefono"], ["company", "Azienda"], ["role", "Mansione"],
            ].map(([k, l]) => (
              <div key={k} className={k === "email" || k === "company" ? "col-span-2" : ""}>
                <Label className="text-xs">{l}</Label>
                <Input value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                  type={k === "birth_date" ? "date" : k === "email" ? "email" : "text"}
                  data-testid={`enrollment-input-${k}`} />
              </div>
            ))}
          </div>
          <div className="mt-2 p-3 rounded-lg border border-amber-200 bg-amber-50/60">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm text-slate-800">
                <GraduationCap className="w-4 h-4 text-amber-600" />
                <span className="font-semibold">Richiede CFP (Crediti Formativi Professionali)</span>
              </div>
              <Switch
                checked={!!form.requires_cfp}
                disabled={!dialog.cfpEnabled}
                onCheckedChange={(v) => setForm((f) => ({ ...f, requires_cfp: !!v }))}
                data-testid="enrollment-cfp-switch"
              />
            </div>
            {!dialog.cfpEnabled && (
              <div className="text-xs text-slate-500 mt-2">Questo corso non prevede il rilascio di CFP.</div>
            )}
            {form.requires_cfp && dialog.cfpEnabled && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Profilo professionale *</Label>
                  <Select
                    value={form.professional_profile || ""}
                    onValueChange={(v) => setForm((f) => ({ ...f, professional_profile: v }))}
                  >
                    <SelectTrigger data-testid="enrollment-cfp-profile">
                      <SelectValue placeholder="Seleziona profilo" />
                    </SelectTrigger>
                    <SelectContent>
                      {(dialog.cfpProfiles || []).map((pp) => (
                        <SelectItem key={pp} value={pp}>{pp}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Provincia *</Label>
                  <Input
                    value={form.professional_province || ""}
                    onChange={(e) => setForm((f) => ({ ...f, professional_province: e.target.value }))}
                    placeholder="Es. Roma, Milano, RM, MI…"
                    data-testid="enrollment-cfp-province"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, courseId: null, editionId: null, cfpEnabled: false, cfpProfiles: [] })}>Annulla</Button>
            <Button onClick={addEnrollment} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="save-enrollment">Salva iscritto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Pagamento ordine {order.order_number}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div className="flex justify-between font-display font-bold text-lg">
              <span>Totale da pagare</span>
              <span data-testid="pay-total">{eur(order.total)}</span>
            </div>
            {order.payment_category === "elearning" ? (
              <>
                <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800" data-testid="pay-credit-info">
                  Corso E-learning: pagamento con <strong>Credito Partner</strong>. Altre modalità non disponibili.
                </div>
                <Button onClick={payWithCredit} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pay-with-credit">
                  <CreditCard className="w-4 h-4 mr-1" /> Paga con Credito Partner · {eur(order.total)}
                </Button>
              </>
            ) : (
              <>
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700">
                  Corso in {order.items?.[0]?.modality === "aula" ? "Aula" : "Videoconferenza"}: paga con <strong>PayPal</strong> oppure <strong>Bonifico Bancario</strong>.
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-amber-600 font-bold mb-2">Carta o PayPal</div>
                  <PayPalPayment order={order} onPaid={onPaypalPaid} onMockPay={() => pay("paypal")} />
                </div>
                <div className="relative py-2">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                  <div className="relative flex justify-center"><span className="bg-white px-3 text-xs uppercase tracking-widest text-slate-400">oppure</span></div>
                </div>
                <BankTransferPayment order={order} onRequested={async () => { setPayOpen(false); await load(); }} />
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)} data-testid="pay-cancel">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
